import { useState } from 'react'
import { getSocNeedsYou, getSocSources, type SocToday } from '../../lib/api'
import { useAuthStore } from '../../stores/authStore'
import { CouldNotSee, SocShell, btnBrass, card, kicker, when } from './SocShell'
import { SocQuestionCard } from './SocQuestion'
import { TellSocForm } from './TellSoc'
import {
  DarkWebFindings,
  NOTE_PLACEHOLDER,
  readoutParagraphs,
  useSocRoute,
  SectionState,
  StaleReadBanner,
  staleRead,
} from './OwnerHome'
import { cn } from '../../lib/utils'

// A plain member's page (docs/design/customer-page/Member.dc.html), phone
// first: their own question, their own account, a note box. /soc/today said
// scope "self"; every route below already returns only this member's rows.
// This page never fetches /soc/people or /soc/actions.
//
// A member cannot answer the analyst's question about themselves (the route
// says 403: the owner answers it). Only a context question is answerable
// here, so only a context question gets an answer box.

const ONLY_YOURS = 'You see only your own account.'

export function MemberHome({ today }: { today: SocToday }) {
  const principal = useAuthStore((s) => s.principal)
  const needsYou = useSocRoute(getSocNeedsYou)
  const sources = useSocRoute(getSocSources)
  const [writing, setWriting] = useState(false)
  const paragraphs = readoutParagraphs(today.readout)
  const readAt = when(today.read_at, 'HH:mm, EEE d MMM')
  const stale = staleRead(today)
  const s = sources.data
  const cannotSee = s ? Array.from(new Set(s.accounts.flatMap((a) => a.cannot_see))) : []

  return (
    // BRIEF-E5d: a member's home wears the one customer header too.
    <SocShell current="today" mainClassName="mx-auto box-border flex w-full max-w-[560px] flex-col gap-4 p-5">
      <div className="contents" data-testid="member-home">
        <p className="m-0 text-[13px] text-[#6b5a3e]">
          {[principal?.display_name, `${today.soc_team_name} watches your account`]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <StaleReadBanner sentence={stale.banner} />
        <section aria-label="Question for you" className="flex flex-col gap-3">
          <SectionState state={needsYou} what="your questions" />
          {needsYou.data && needsYou.data.items.length === 0 && (
            <p className="m-0 text-[15px] text-[#6b5a3e]">{ONLY_YOURS}</p>
          )}
          {needsYou.data?.items.map((item) => (
            <SocQuestionCard
              key={item.ref}
              item={item}
              variant="member"
              canAnswer={item.kind === 'context_question'}
            />
          ))}
          <CouldNotSee items={needsYou.data?.could_not_see} className="text-[13px]" />
        </section>

        <section
          aria-label="Your account"
          className={cn(card, 'flex flex-col gap-2.5 p-[18px]')}
        >
          <p className={cn(kicker, 'm-0')}>Your account</p>
          {paragraphs.map((p, i) => (
            <p key={i} className="m-0 text-[15px] leading-snug whitespace-pre-line">
              {p}
            </p>
          ))}
          <div className="flex justify-between gap-3 text-[15px]">
            <span>Last read</span>
            <strong className="text-right">{readAt ?? 'No read recorded'}</strong>
          </div>
          {today.accounts_not_read.map((n) => (
            <p key={n.person} className="m-0 text-[15px] text-[#6b5a3e]">
              {n.person}: {n.reason}
            </p>
          ))}
          <SectionState state={sources} what="what we watch for you" />
          {s?.accounts.map((a) => (
            <div key={a.account} className="flex justify-between gap-3 text-[15px]">
              <span>{a.account}</span>
              <strong className="text-right">
                {when(a.last_successful_poll)
                  ? `read ${when(a.last_successful_poll)}`
                  : 'never read successfully'}
              </strong>
            </div>
          ))}
          {s?.dark_web.domains.map((d) => (
            <div key={d.domain} className="flex justify-between gap-3 text-[15px]">
              <span>Dark web, {d.domain}</span>
              <strong className="text-right">{d.result}</strong>
            </div>
          ))}
          {s && (
            <DarkWebFindings
              findings={s.dark_web.findings}
              intro={`Found for you in the last ${s.dark_web.lookback_days} days`}
            />
          )}
          {cannotSee.length > 0 && (
            <p className="m-0 text-[13px] text-[#6b5a3e]">Cannot see: {cannotSee.join('; ')}.</p>
          )}
          <CouldNotSee items={stale.couldNotSee} className="text-[13px]" />
          <CouldNotSee items={s?.could_not_see} className="text-[13px]" />
          <p className="m-0 text-[13px] text-[#6b5a3e]">{ONLY_YOURS}</p>
        </section>

        <section
          aria-label="Tell your SOC"
          className="flex flex-col gap-2.5 rounded-[10px] border border-navy bg-navy p-[18px] text-cream"
        >
          <p className={cn(kicker, 'm-0 text-brass-light')}>Tell your SOC something</p>
          <p className="m-0 text-[15px] leading-snug text-[#d9cdb8]">
            Travelling, a new phone, a new app you need. One line is enough.
          </p>
          {writing ? (
            <TellSocForm placeholder={NOTE_PLACEHOLDER} compact />
          ) : (
            <button type="button" className={btnBrass} onClick={() => setWriting(true)}>
              Write a note
            </button>
          )}
        </section>
      </div>
    </SocShell>
  )
}
